/* eslint-disable no-underscore-dangle */
import React, { FC, ReactNode, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { Form, Formik, FormikProps } from 'formik';
import { styled } from 'linaria/react';
import { usePathname } from 'next/navigation';
import { checkIsServiceNonTopUp, SteamAccountPayee, TspPageDenomination, TspPagePayee, useCyberStore } from '@entities/tsp';
import { PaymentFormValues, PaymentMethod } from '@entities/payment';
import { CountryWithIcon } from '@entities/country';
import { ServerOption } from '@entities/server';
import { PROMOCODES_FROM_2K, WIDGET_PROMOODE } from '@entities/promocode';
import { CommissionDataByServiceIdType, DenominationAmountByServiceIdType, findDenominationByServiceId, findFirstAvailableDenominationByCountry, GAME_PERCENT_FEE_SERVICE_IDS, resolveDenominationCountry, transformAmountByCommissionData } from '@entities/denomination';
import { useGetPrice } from '@entities/voucher';
import { paymentQrShowGtm } from '@shared/lib/gtm/redesign';
import { Partners, usePartner } from '@shared/lib/partner';
import { ModalProps } from '@shared/ui/organisms';
import { Amount } from '@root/api/ump-ewallet';
import { TYPE_OF_FIELDS_TO_BE_MAPPED } from '../config/fieldsParams';
import { initialPaymentFormValues } from '../config/form';
import { getPaymentFormValidation } from '../config/getPaymentFormValidation';
import { useGetCommissions } from '../model/useGetCommissions';
import { usePayBySBP } from '../model/usePayBySBP';
import { usePayByMtsAccount } from '../model/usePayByMtsAccount';
import { useRequestId } from '../lib/useRequestId';
import { useResultModal } from '../lib/useResultModal';
import { getClientTotalAmount } from '../lib/getClientTotalAmount';
import { getTspEventContent } from '../lib/getTspEventContent';
export interface TspInfoProps {
  title: string;
  icon?: string;
  isVouchers?: boolean;
  params?: Array<{
    name: string;
    description?: string;
  }>;
  activeService?: SteamAccountPayee;
}
export interface DenominationListProps {
  payee: TspPagePayee;
  countryVouchers: CountryWithIcon[];
  denominationsByCountry: Record<string, TspPageDenomination[]>;
  pricesByServiceId: DenominationAmountByServiceIdType;
}
export interface TspPaymentFormProps {
  payee: TspPagePayee;
  servers: ServerOption[];
  countryVouchers: CountryWithIcon[];
  denominations: Record<string, TspPageDenomination[]>;
  pricesByServiceId: DenominationAmountByServiceIdType;
  isGetPaymentTermsLoading: boolean;
  isGetPriceLoading: boolean;
  isPaymentloading: boolean;
  isAmountInfoLoading: boolean;
  getCommissions: (amount: Required<Pick<Amount, 'base' | 'fee' | 'total'>>) => void;
}
export interface ResultModalProps extends ModalProps {
  payee: TspPagePayee;
}
interface SbpQrModalProps {
  payee: TspPagePayee;
  open: boolean;
  handleClose: () => void;
  qrLink: string;
  description: string;
}
interface Props {
  h1: string;
  payee: TspPagePayee;
  countryVouchers: {
    country: string;
    icon: string;
  }[];
  denominationsByCountry: Record<string, TspPageDenomination[]>;
  pricesByServiceId: DenominationAmountByServiceIdType;
  servers: ServerOption[];
  commissionData: CommissionDataByServiceIdType;
  renderTspInfo: (props: TspInfoProps) => ReactNode;
  renderDenominationList: (props: DenominationListProps) => ReactNode;
  renderPaymentForm: (props: TspPaymentFormProps) => ReactNode;
  // renderReplenishmentInfo: (props: ReplenishmentInfoProps) => ReactNode
  // renderAccountInfo: (props: AccountInfoProps) => ReactNode
  // renderPaymentInfo: (props: PaymentInfoProps) => ReactNode
  renderQrModal: (props: SbpQrModalProps) => ReactNode;
  renderResultModal: (props: ResultModalProps) => ReactNode;
}
const TspPaymentForm: FC<Props> = ({
  h1,
  payee,
  countryVouchers,
  denominationsByCountry,
  pricesByServiceId: initialPricesByServiceId,
  servers,
  commissionData,
  renderTspInfo,
  renderDenominationList,
  renderPaymentForm,
  renderQrModal,
  renderResultModal
}) => {
  const isVouchers = true;
  const router = useRouter();
  const pathname = usePathname();
  const paymentFormRef = useRef<FormikProps<PaymentFormValues>>(null);
  const priceRequestIdRef = useRef(0);
  const [pricesByServiceId, setPricesByServiceId] = useState(initialPricesByServiceId);
  const {
    partner
  } = usePartner();
  const {
    paymentMethod,
    isPaymentloading,
    amountInfo,
    isAmountInfoLoading,
    activeCountry,
    setActiveCountry,
    activeDenomination,
    setActiveDenomination,
    setIsPromocodeActivated,
    lastActivatedPromocode,
    requestID,
    pcekMdOrder,
    isPromocodeActivated
  } = useCyberStore();
  const serviceIdQuery = Array.isArray(router.query.serviceId) ? router.query.serviceId[0] : router.query.serviceId;
  const [id, setId] = useRequestId();
  const {
    isResultModalActive,
    handleResultModalClose
  } = useResultModal();
  const {
    getCommissions,
    isGetPaymentTermsLoading,
    cancelGetPaymentTermsRequest
  } = useGetCommissions({
    isVouchers,
    payee,
    paymentFormRef,
    commissionData
  });
  const {
    getPrice,
    cancelPriceRequest,
    isGetPriceLoading,
    activeDenominationParams
  } = useGetPrice(activeDenomination && 'params' in activeDenomination ? activeDenomination : null);
  const {
    qrLink,
    qrcId,
    payBySBP,
    cancelSBPPayment
  } = usePayBySBP({
    isVouchers,
    payee,
    id,
    setId
  });
  const {
    payByMtsAccount,
    isPaymentByMtsAccountLoading
  } = usePayByMtsAccount({
    isVouchers,
    payee
  });
  const isPubgMobile = payee.features?.tsp_url === 'pubg-mobile';
  const isNonTopUp = checkIsServiceNonTopUp(true, payee);
  const isZeroFee = Boolean(payee.customParams?.isZeroFee);
  const isGameWithPercentFee = GAME_PERCENT_FEE_SERVICE_IDS.includes(payee.features?.tsp_url ?? '');
  const activePrice = activeDenomination ? pricesByServiceId[activeDenomination.serviceId] : undefined;
  useEffect(() => {
    setPricesByServiceId(initialPricesByServiceId);
  }, [initialPricesByServiceId]);

  // для СБП terms не нужны; сумму берём из pricesByServiceId сразу.
  // для MTS_ACCOUNT сначала getPrice, затем paymentTerms — см. эффект ниже
  useEffect(() => {
    if (paymentMethod === PaymentMethod.MTS_ACCOUNT) {
      return;
    }
    if (activeCountry && activeDenomination && activePrice) {
      cancelGetPaymentTermsRequest();
      getCommissions(activePrice);
    }
  }, [activeCountry, activeDenomination, activePrice, paymentMethod]);

  // инициализация активной страны при маунте/переходе на стр другого сервиса
  useEffect(() => {
    setActiveCountry(resolveDenominationCountry(denominationsByCountry, countryVouchers, null, serviceIdQuery));
    return () => {
      setActiveCountry(null);
    };
  }, [countryVouchers, payee, serviceIdQuery]);

  // очистка выбранной страны при переходе на другую страницу
  useEffect(() => () => {
    setActiveCountry(null);
  }, [pathname]);

  // инициализация активного номинала при маунте/выборе страны
  useEffect(() => {
    cancelGetPaymentTermsRequest();
    if (!activeCountry) {
      setActiveDenomination(null);
      return;
    }
    const denominationFromQuery = findDenominationByServiceId(denominationsByCountry, serviceIdQuery);
    const countryFromQuery = denominationFromQuery ? denominationFromQuery.features?.country_web as string ?? 'noRegion' : undefined;
    if (denominationFromQuery && countryFromQuery === activeCountry) {
      setActiveDenomination(denominationFromQuery);
      return;
    }

    // выбираем первый доступный номинал; если цены ещё не загружены — берём первый из списка
    const firstAvailable = findFirstAvailableDenominationByCountry(denominationsByCountry, activeCountry, pricesByServiceId);
    setActiveDenomination(firstAvailable ?? denominationsByCountry[activeCountry]?.[0] ?? null);
  }, [activeCountry, payee, serviceIdQuery]);

  // получение актуальной цены при выборе номинала.
  // для MTS_ACCOUNT paymentTerms вызывается только после getPrice
  useEffect(() => {
    const serviceId = activeDenomination?.serviceId;
    if (!serviceId || !activeDenominationParams?.serviceCode.value) {
      return undefined;
    }
    const requestId = priceRequestIdRef.current + 1;
    priceRequestIdRef.current = requestId;
    if (paymentMethod === PaymentMethod.MTS_ACCOUNT) {
      cancelGetPaymentTermsRequest();
    }
    cancelPriceRequest();
    const requestPaymentTerms = (amount: Required<Pick<Amount, 'base' | 'fee' | 'total'>> | undefined) => {
      if (paymentMethod !== PaymentMethod.MTS_ACCOUNT || !amount?.base) {
        return;
      }
      getCommissions(amount);
    };
    getPrice().then(({
      data
    }) => {
      if (requestId !== priceRequestIdRef.current) {
        return;
      }
      const nextBase = data?.price ?? '';
      const nextAmount = transformAmountByCommissionData(nextBase, commissionData, isPubgMobile, isNonTopUp, isZeroFee, isGameWithPercentFee);
      setPricesByServiceId(prev => {
        if (prev[serviceId]?.base === nextBase) {
          return prev;
        }
        return {
          ...prev,
          [serviceId]: nextAmount
        };
      });
      requestPaymentTerms(nextAmount);
    }).catch(() => {
      if (requestId !== priceRequestIdRef.current) {
        return;
      }
      requestPaymentTerms(pricesByServiceId[serviceId]);
    });
    return () => {
      cancelPriceRequest();
    };
  }, [activeDenomination?.serviceId, activeDenominationParams?.serviceCode.value, paymentMethod]);
  const handleServiceFormSubmitData = (values: PaymentFormValues) => {
    if (isAmountInfoLoading) return;
    if (values.promocode.toUpperCase() === WIDGET_PROMOODE && Number(values.price) < 2500) {
      setIsPromocodeActivated(false);
      paymentFormRef.current?.setFieldError('promocode', 'Условия не выполнены');
      return;
    }
    if (PROMOCODES_FROM_2K?.includes(lastActivatedPromocode) && Number(values.price) < 2000) {
      setIsPromocodeActivated(false);
      paymentFormRef.current?.setFieldError('promocode', 'Промокод действует только при пополнении от 2.000 ₽');
      return;
    }
    const isFromPubgGiveawayPage = router.query.from === 'rozygrysh-prizov-pubg-mobile';
    if (isFromPubgGiveawayPage) {
      window.recordTier?.push({
        eventName: 'landingpageClick',
        tracking_id: '128635520'
      });
      window._tmr?.push({
        type: 'reachGoal',
        id: 3712315,
        goal: 'click_purchase'
      });
    }
    if (partner === Partners.DEFAULT) {
      window?._tmr?.push({
        type: 'reachGoal',
        id: 3712315,
        goal: 'click_pay_games_cyber'
      });
    }
    if (paymentMethod === PaymentMethod.SBP) {
      payBySBP(values);
    } else {
      payByMtsAccount();
    }
  };
  useEffect(() => {
    if (!qrLink || paymentMethod !== PaymentMethod.SBP) {
      return;
    }
    const amount = String(getClientTotalAmount(amountInfo, isPromocodeActivated, false) || '').replace(/\s/g, '');
    paymentQrShowGtm(getTspEventContent(payee), {
      productName: payee.providerName,
      amount,
      requestId: requestID || id,
      transactionId: qrcId || pcekMdOrder
    });
    // qrLink — момент показа QR, остальные поля читаются из текущего рендера
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qrLink]);
  const fields = payee?.params?.filter(param => TYPE_OF_FIELDS_TO_BE_MAPPED.includes(param.name));
  return <Formik enableReinitialize validateOnChange innerRef={paymentFormRef} initialValues={initialPaymentFormValues} validationSchema={getPaymentFormValidation({
    fields,
    partner
  })} onSubmit={handleServiceFormSubmitData}>
      <StyledForm>
        <ContentWrapper>
          <TspInfoWrapper>
            {renderTspInfo({
            title: h1,
            icon: payee?.icon,
            isVouchers,
            params: payee?.params
          })}
          </TspInfoWrapper>
          <DenominationListWrapper>
            {renderDenominationList({
            payee,
            countryVouchers,
            denominationsByCountry,
            pricesByServiceId
          })}
          </DenominationListWrapper>
          <PaymentFormWrapper>
            {renderPaymentForm({
            payee,
            servers,
            countryVouchers,
            denominations: denominationsByCountry,
            pricesByServiceId,
            isGetPaymentTermsLoading,
            isGetPriceLoading,
            isPaymentloading: isPaymentloading || isPaymentByMtsAccountLoading,
            isAmountInfoLoading: isAmountInfoLoading || isGetPaymentTermsLoading,
            getCommissions
          })}
          </PaymentFormWrapper>
        </ContentWrapper>
        {renderQrModal({
        payee,
        open: !!qrLink && paymentMethod === PaymentMethod.SBP,
        qrLink,
        description: h1,
        handleClose: cancelSBPPayment
      })}
        {renderResultModal({
        payee,
        open: isResultModalActive,
        onClose: handleResultModalClose
      })}
      </StyledForm>
    </Formik>;
};
export default TspPaymentForm;
const _exp = /*#__PURE__*/() => Form;
const StyledForm = /*#__PURE__*/styled(_exp())({
  name: "StyledForm",
  class: "sf29tsz",
  propsAsIs: true
});
const ContentWrapper = /*#__PURE__*/styled('div')({
  name: "ContentWrapper",
  class: "c1whlbvt",
  propsAsIs: false
});
const TspInfoWrapper = /*#__PURE__*/styled('section')({
  name: "TspInfoWrapper",
  class: "t1aw2m5n",
  propsAsIs: false
});
const DenominationListWrapper = /*#__PURE__*/styled('section')({
  name: "DenominationListWrapper",
  class: "d5568og",
  propsAsIs: false
});
const PaymentFormWrapper = /*#__PURE__*/styled('section')({
  name: "PaymentFormWrapper",
  class: "p19j1dr6",
  propsAsIs: false
});

require("./TspPaymentForm.linaria.module.css!=!../../../../../node_modules/linaria/node_modules/@linaria/webpack4-loader/lib/outputCssLoader.js?cacheProvider=!./TspPaymentForm.tsx");